/**
 * EncoreFormRenderer schema definitions for the profile page edit mode.
 * Uses 16-column grid layout with equal 2-column split (columns: 8 each).
 * Labels are localized via the provided translate function.
 */

import { GENDER_OPTIONS } from './profileConstants';

type TranslateFn = (key: string, defaultValue: string) => string;

/**
 * Builds the personal info schema with localized labels.
 */
export function buildPersonalInfoSchema(t: TranslateFn) {
  return {
    options: { showNavigation: false },
    components: [
      {
        type: 'textfield',
        id: 'fullName',
        key: 'name',
        label: t('userDetails.fullName', 'Full Name'),
        disabled: true,
        layout: { row: 'Row_1', columns: 8 },
      },
      {
        type: 'textfield',
        id: 'emailAddress',
        key: 'profileInformation.emailAddress',
        label: t('userDetails.emailAddress', 'Email Address'),
        disabled: true,
        layout: { row: 'Row_1', columns: 8 },
      },
      {
        type: 'textfield',
        id: 'phoneNumber',
        key: 'profileInformation.phoneNumber',
        label: t('userDetails.phoneNumber', 'Phone Number'),
        layout: { row: 'Row_2', columns: 8 },
        validate: { pattern: '^\\+?[1-9]\\d{1,19}$', maxLength: 20 },
      },
      {
        type: 'textfield',
        id: 'employeeId',
        key: 'id',
        label: t('userDetails.employeeId', 'Employee ID'),
        disabled: true,
        layout: { row: 'Row_2', columns: 8 },
      },
      {
        type: 'select',
        id: 'gender',
        key: 'profileInformation.gender',
        label: t('userDetails.gender', 'Gender'),
        layout: { row: 'Row_3', columns: 8 },
        values: GENDER_OPTIONS,
      },
      {
        type: 'datetime',
        subtype: 'date',
        id: 'dateOfBirth',
        key: 'profileInformation.dateOfBirth',
        label: t('userDetails.dateOfBirth', 'Date of Birth'),
        layout: { row: 'Row_3', columns: 8 },
      },
    ],
  };
}

export interface LocaleSettingsSchemaOptions {
  timezoneOptions: Array<{ label: string; value: string }>;
  localeOptions: Array<{ label: string; value: string }>;
}

/**
 * Builds the locale settings schema with dynamic timezone and locale options
 * derived from the entity schema metadata, and localized labels.
 */
export function buildLocaleSettingsSchema(
  options: LocaleSettingsSchemaOptions,
  t: TranslateFn = (_, d) => d
) {
  return {
    options: { showNavigation: false },
    components: [
      {
        type: 'select',
        id: 'timezone',
        key: 'preferences.timezone',
        label: t('userDetails.timezone', 'Timezone'),
        layout: { row: 'Row_1', columns: 8 },
        values: options.timezoneOptions,
      },
      {
        type: 'select',
        id: 'language',
        key: 'preferences.localeIdentifier',
        label: t('userDetails.language', 'Language'),
        layout: { row: 'Row_1', columns: 8 },
        values: options.localeOptions,
      },
    ],
  };
}

/**
 * Pre-processes entity data for the personal information form.
 * Extracts the `.local` string from the temporal dateOfBirth object
 * so the datetime field receives a plain date string.
 */
export function buildPersonalInfoInitialData(
  entityData: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!entityData) {
    return {};
  }

  const profileInformation = entityData.profileInformation as
    | Record<string, unknown>
    | undefined;
  const rawDob = profileInformation?.dateOfBirth;
  const dateOfBirth =
    typeof rawDob === 'string'
      ? rawDob
      : ((rawDob as { local?: string } | undefined)?.local ?? null);

  return {
    ...entityData,
    profileInformation: {
      ...profileInformation,
      dateOfBirth,
    },
  };
}

/**
 * Converts the form's flat dateOfBirth string back into the temporal
 * object expected by the API: `{ local: "YYYY-MM-DD", timeZone: "..." }`.
 */
export function buildDateOfBirthPayload(
  dateOfBirthValue: string | null | undefined,
  configuredTimezone: string
): { local: string; timeZone: string } | null {
  if (!dateOfBirthValue) {
    return null;
  }
  return { local: dateOfBirthValue, timeZone: configuredTimezone };
}
